import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '@/lib/supabase';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { SearchablePickerField } from '@/components/ui/SearchablePickerField';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Profile, ProfileUpdate } from '@/lib/database.types';
import { fullNameMissingAccents } from '@/lib/format';

type Props = {
  initial: Profile | null;
  submitLabel?: string;
  hideSubmitButton?: boolean;
  onSubmit: (patch: ProfileUpdate) => Promise<void>;
  /** Ιστορικά ήδη επαληθευμένο κινητό (ενημερωτικό μόνο — η επαλήθευση
   * κινητού μέσω SMS καταργήθηκε, βλ. commit "remove SMS phone verification"). */
  phoneVerified?: boolean;
  identityVerified?: boolean;
  /** Αν true, εμφανίζεται το πεδίο Ειδικότητας (για portfolio) */
  showSpecialty?: boolean;
  /** Αν true, κρύβει το πεδίο ονόματος (εμφανίζεται αλλού στην οθόνη) */
  hideName?: boolean;
  /** Ενεργοποιημένοι τρόποι επικοινωνίας (εμφανίζονται κάτω από το τηλέφωνο) */
  commApps?: string[];
  onCommAppToggle?: (app: string) => Promise<void>;
  /** Καλείται όταν αλλάζει αν υπάρχουν ανεπάρτητες αλλαγές */
  onDirtyChange?: (dirty: boolean) => void;
};

function useCommOptions(): { key: string; label: string; icon: keyof typeof Ionicons.glyphMap }[] {
  const { t } = useTranslation();
  return [
    { key: 'viber',     label: 'Viber',                    icon: 'call-outline' },
    { key: 'whatsapp',  label: 'WhatsApp',                  icon: 'logo-whatsapp' },
    { key: 'telegram',  label: 'Telegram',                  icon: 'paper-plane-outline' },
    { key: 'signal',    label: 'Signal',                    icon: 'lock-closed-outline' },
    { key: 'call',      label: t('profile.comm_call'),      icon: 'phone-portrait-outline' },
    { key: 'email',     label: 'Email',                     icon: 'mail-outline' },
  ];
}

export type ProfileFormHandle = { submit: () => void };

export const ProfileForm = forwardRef<ProfileFormHandle, Props>(function ProfileForm({
  initial,
  submitLabel,
  hideSubmitButton = false,
  onSubmit,
  phoneVerified = false,
  identityVerified = false,
  showSpecialty = false,
  hideName = false,
  commApps,
  onCommAppToggle,
  onDirtyChange,
}, ref) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const COMM_OPTIONS = useCommOptions();
  const resolvedSubmitLabel = submitLabel ?? t('common.save');

  const [firstName, setFirstName] = useState(initial?.first_name ?? initial?.full_name?.split(' ')[0] ?? '');
  const [lastName, setLastName] = useState(initial?.last_name ?? initial?.full_name?.split(' ').slice(-1)[0] ?? '');
  const [nickname, setNickname] = useState((initial as any)?.nickname ?? '');
  const [showNicknameField, setShowNicknameField] = useState(!!(initial as any)?.nickname?.trim());
  const nameLocked = !!(initial as any)?.full_name_locked;
  const nameMissingAccent = useMemo(
    () => fullNameMissingAccents(firstName) || fullNameMissingAccents(lastName),
    [firstName, lastName],
  );
  const nicknameUpdatedAt = (initial as any)?.nickname_updated_at ? new Date((initial as any).nickname_updated_at) : null;
  const nicknameDaysRemaining = nicknameUpdatedAt
    ? Math.max(0, 30 - Math.floor((Date.now() - nicknameUpdatedAt.getTime()) / 86400000))
    : 0;
  const nicknameLocked = nicknameDaysRemaining > 0;
  const [bio, setBio] = useState(initial?.bio ?? '');
  const [specialty, setSpecialty] = useState(initial?.specialty ?? '');
  const [locationId, setLocationId] = useState<number | null>((initial as any)?.location_id ?? null);
  const [professionCategoryId, setProfessionCategoryId] = useState<number | null>((initial as any)?.profession_category_id ?? null);
  const [locations, setLocations] = useState<{ id: number; name: string }[]>([]);
  const [professionCategories, setProfessionCategories] = useState<{ id: number; name: string }[]>([]);
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void supabase.from('greek_locations').select('id, name').order('name')
      .then(({ data }) => { if (data) setLocations(data as { id: number; name: string }[]); });
    void supabase.from('profession_categories').select('id, name').order('name')
      .then(({ data }) => { if (data) setProfessionCategories(data as { id: number; name: string }[]); });
  }, []);

  // Τηλέφωνο με κωδικό χώρας
  // Sorted longest-first ώστε +357 να ελεγχθεί πριν το +35.
  const KNOWN_DIALS = ['+357', '+359', '+355', '+351', '+49', '+44', '+47', '+46', '+45', '+43', '+41', '+40', '+39', '+34', '+33', '+32', '+31', '+61', '+30', '+1'];
  const [dialCode, setDialCode] = useState(() => {
    const p = initial?.phone ?? '';
    return KNOWN_DIALS.find((d) => p.startsWith(d)) ?? '+30';
  });
  const [nationalPhone, setNationalPhone] = useState(() => {
    const p = initial?.phone ?? '';
    if (!p) return '';
    const dial = KNOWN_DIALS.find((d) => p.startsWith(d));
    return dial ? p.slice(dial.length) : p;
  });

  function fullPhone() {
    return `${dialCode}${nationalPhone.replace(/^0+/, '')}`;
  }

  // Κεφαλαιοποιεί κάθε λέξη ξεχωριστά (π.χ. "Γεώργιος Κωνσταντίνος", όχι μόνο
  // την πρώτη λέξη) — επιτρέπει δεύτερο/μεσαίο όνομα στο ίδιο πεδίο.
  function capitalizeWord(s: string): string {
    if (!s) return s;
    return s
      .split(' ')
      .map((word) => {
        if (!word) return word;
        // toLocaleLowerCase('el') + αντικατάσταση final sigma (ς→σ) σε μη-τελική θέση
        const rest = word.slice(1).toLocaleLowerCase('el').replace(/ς(?=\p{L})/gu, 'σ');
        return word.charAt(0).toLocaleUpperCase('el') + rest;
      })
      .join(' ');
  }

  const isDirty = useMemo(() => {
    const initPhone = initial?.phone ?? '';
    const initDial = KNOWN_DIALS.find((d) => initPhone.startsWith(d)) ?? '+30';
    const initNational = initDial ? initPhone.slice(initDial.length) : initPhone;
    if (!hideName && (firstName !== (initial?.first_name ?? initial?.full_name?.split(' ')[0] ?? ''))) return true;
    if (!hideName && (lastName !== (initial?.last_name ?? initial?.full_name?.split(' ').slice(-1)[0] ?? ''))) return true;
    if (bio !== (initial?.bio ?? '')) return true;
    if (dialCode !== initDial || nationalPhone !== initNational) return true;
    return false;
  }, [firstName, lastName, bio, dialCode, nationalPhone, initial, hideName]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  function validatePart(v: string, label: string): string | null {
    const val = v.trim();
    if (!val) return t('profile.validation_required', { label });
    if (val.length > 40) return t('profile.validation_tooLong', { label });
    if (/[^a-zA-ZΑ-Ωα-ωάέήίόύώΆΈΉΊΌΎΏΪΫΐΰϊϋ\-\s]/.test(val))
      return t('profile.validation_lettersOnly');
    if (/\s{2,}/.test(val)) return t('profile.validation_lettersOnly');
    if (val.length < 2) return t('profile.validation_tooShort', { label });
    return null;
  }

  async function handleSubmit() {
    setFormError(null);
    if (!hideName) {
      const fnErr = validatePart(firstName, t('profile.label_firstName'));
      const lnErr = validatePart(lastName, t('profile.label_lastName'));
      if (fnErr || lnErr) { setNameError(fnErr ?? lnErr ?? null); return; }
    }
    setNameError(null);
    setLoading(true);
    try {
      const phone = nationalPhone.trim() ? fullPhone() : null;
      const fn = capitalizeWord(firstName.trim());
      const ln = capitalizeWord(lastName.trim());
      await onSubmit({
        ...(hideName ? {} : {
          first_name: fn,
          last_name: ln,
          full_name: `${fn} ${ln}`,
        }),
        phone,
        bio: bio.trim() || null,
        ...(showSpecialty ? { specialty: specialty.trim() || null } : {}),
        nickname: nickname.trim() || null,
        location_id: locationId,
        profession_category_id: professionCategoryId,
      } as any);
    } catch (e) {
      setFormError(getErrorMessage(e, t('profile.error_genericRetry')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <View style={styles.form}>
      {!hideName && (
        <>
          <Input
            label={t('profile.field_firstName')}
            value={firstName}
            onChangeText={setFirstName}
            onBlur={() => setFirstName((v) => capitalizeWord(v))}
            error={nameError}
            placeholder={t('placeholder.firstName')}
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="givenName"
            returnKeyType="next"
            editable={!nameLocked}
          />
          <Input
            label={t('profile.field_lastName')}
            value={lastName}
            onChangeText={setLastName}
            onBlur={() => setLastName((v) => capitalizeWord(v))}
            placeholder={t('placeholder.lastName')}
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="familyName"
            returnKeyType="next"
            editable={!nameLocked}
          />
          {nameLocked && (
            <Text style={styles.nameLockHint}>
              {t('profile.nameLockHint')}
            </Text>
          )}
          {!nameLocked && nameMissingAccent && (
            <Text style={styles.nameAccentHint}>
              {t('profile.nameAccentHint')}
            </Text>
          )}
          <Pressable
            style={styles.nicknameCheckboxRow}
            onPress={() => setShowNicknameField((v) => !v)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: showNicknameField }}
          >
            <View style={[styles.checkbox, showNicknameField && styles.checkboxOn]}>
              {showNicknameField && <Ionicons name="checkmark" size={14} color="#fff" />}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.checkboxLabel}>{t('profile.nicknameCheckboxLabel')}</Text>
              <Text style={styles.checkboxSublabel}>{t('profile.nicknameCheckboxSublabel')}</Text>
            </View>
          </Pressable>
          {showNicknameField && (
            <>
              <Input
                label={t('profile.field_nickname')}
                value={nickname}
                onChangeText={setNickname}
                placeholder={t('profile.placeholder_nickname')}
                autoCapitalize="words"
                autoCorrect={false}
                returnKeyType="next"
                maxLength={30}
                editable={!nicknameLocked}
              />
              <Text style={styles.nameLockHint}>{t('profile.hint_nickname')}</Text>
              {nicknameLocked ? (
                <Text style={styles.nameLockHint}>
                  {t('profile.nicknameLockHint', { count: nicknameDaysRemaining })}
                </Text>
              ) : nickname.trim() !== ((initial as any)?.nickname ?? '').trim() ? (
                <Text style={styles.nicknameWarning}>{t('profile.nicknameWillLockWarning')}</Text>
              ) : !!nickname.trim() && (
                <Text style={styles.nameLockHint}>
                  {t('profile.nicknamePreview', { nickname: nickname.trim() })}
                </Text>
              )}
            </>
          )}

          {/* Τοποθεσία */}
          {locations.length > 0 && (
            <SearchablePickerField
              label={t('profile.field_location')}
              value={locationId}
              options={locations}
              onChange={setLocationId}
            />
          )}

          {/* Κλάδος */}
          {professionCategories.length > 0 && (
            <SearchablePickerField
              label={t('profile.field_professionCategory')}
              value={professionCategoryId}
              options={professionCategories}
              onChange={setProfessionCategoryId}
            />
          )}
        </>
      )}

      <View style={styles.phoneSection}>
        <View style={styles.phoneLabelRow}>
          <Text style={styles.phoneLabel}>{t('profile.field_phone')}</Text>
          <View style={styles.levelPills}>
            <View style={[styles.levelPill, identityVerified && styles.levelPillDone]}>
              <Ionicons
                name={identityVerified ? 'checkmark-circle' : 'id-card-outline'}
                size={11}
                color={identityVerified ? colors.success : colors.textMuted}
              />
              <Text style={[styles.levelPillText, identityVerified && styles.levelPillTextDone]}>
                {t('profile.level2_pill')}
              </Text>
            </View>
          </View>
        </View>
        <PhoneInput
          value={nationalPhone}
          onChangeText={setNationalPhone}
          dialCode={dialCode}
          onChangeDialCode={setDialCode}
        />
        {phoneVerified && (
          <View style={styles.verifiedRow}>
            <Ionicons name="checkmark-circle" size={18} color={colors.primaryLight} />
            <Text style={styles.verifiedText}>{t('profile.phone_verified')}</Text>
          </View>
        )}

        <Text style={styles.phoneHint}>{t('profile.hint_phone_icon')}</Text>
        <Text style={styles.phoneHintSmall}>{t('profile.hint_phone_noVerify')}</Text>
      </View>

      {commApps !== undefined && onCommAppToggle && (
        <View style={styles.commSection}>
          <View style={styles.commTitleRow}>
            <Text style={styles.commTitle}>{t('profile.comm_title')}</Text>
            <Pressable
              onPress={() => appAlert(t('profile.comm_infoTitle'), t('profile.comm_infoBody'))}
              hitSlop={10}
              accessibilityRole="button"
            >
              <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
            </Pressable>
          </View>
          {COMM_OPTIONS.map((opt, i) => {
            const requiresPhone = opt.key !== 'email';
            const disabled = requiresPhone && nationalPhone.trim().length === 0;
            return (
              <Pressable
                key={opt.key}
                style={[styles.commRow, i > 0 && styles.commRowBorder]}
                onPress={() => { if (!disabled) void onCommAppToggle(opt.key); }}
                disabled={disabled}
                accessibilityRole="switch"
              >
                <View style={styles.commLeft}>
                  <Ionicons name={opt.icon} size={17} color={disabled ? colors.textMuted : colors.primary} />
                  <Text style={[styles.commLabel, disabled && styles.commLabelDisabled]}>{opt.label}</Text>
                </View>
                <View style={[styles.commToggle, commApps.includes(opt.key) && styles.commToggleOn]}>
                  <View style={[styles.commKnob, commApps.includes(opt.key) && styles.commKnobOn]} />
                </View>
              </Pressable>
            );
          })}
          {nationalPhone.trim().length === 0 && (
            <Text style={styles.commPhoneHint}>{t('profile.comm_needPhone')}</Text>
          )}
        </View>
      )}

      <Input
        label={t('profile.field_bio')}
        value={bio}
        onChangeText={setBio}
        placeholder={t('profile.placeholder_bio')}
        multiline
        numberOfLines={3}
        style={styles.multiline}
      />

      {showSpecialty && (
        <Input
          label={t('profile.field_specialtyFull')}
          value={specialty}
          onChangeText={setSpecialty}
          placeholder={t('profile.placeholder_specialtyFull')}
        />
      )}

      {!!formError && <Text style={styles.formError}>{formError}</Text>}

      {!hideSubmitButton && <Button title={resolvedSubmitLabel} onPress={handleSubmit} loading={loading} />}
    </View>
  );
});

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    form: {
      gap: spacing.md,
    },
    phoneSection: {
      gap: spacing.xs,
    },
    phoneLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 2,
    },
    phoneLabel: {
      color: colors.textMuted,
      fontSize: 13,
      fontWeight: '600',
    },
    levelPills: {
      flexDirection: 'row',
      gap: 6,
    },
    levelPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 7,
      paddingVertical: 3,
    },
    levelPillDone: {
      borderColor: colors.success,
      backgroundColor: 'transparent',
    },
    levelPillText: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '600',
    },
    levelPillTextDone: {
      color: colors.success,
    },
    verifiedRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingVertical: spacing.sm,
    },
    verifiedText: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    phoneHint: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      marginTop: 2,
    },
    nameLockHint: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      lineHeight: 16,
      marginTop: -4,
    },
    nameAccentHint: {
      color: colors.warning,
      fontSize: fontSize.xs,
      lineHeight: 16,
      marginTop: -4,
    },
    nicknameCheckboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    checkbox: {
      width: 20, height: 20, borderRadius: 5,
      borderWidth: 1.5, borderColor: colors.border,
      alignItems: 'center', justifyContent: 'center',
    },
    checkboxOn: { backgroundColor: colors.primary, borderColor: colors.primary },
    checkboxLabel: {
      color: colors.text,
      fontSize: fontSize.sm,
      flexShrink: 1,
    },
    checkboxSublabel: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      marginTop: 1,
      flexShrink: 1,
    },
    nicknameWarning: {
      color: '#F59E0B',
      fontSize: fontSize.xs,
      lineHeight: 16,
      marginTop: -4,
    },
    phoneHintSmall: {
      color: colors.textMuted,
      fontSize: 11,
      marginTop: 1,
    },
    multiline: {
      height: 96,
      paddingTop: spacing.md,
      textAlignVertical: 'top',
    },
    formError: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
    commSection: {
      gap: spacing.xs,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.md,
      marginTop: spacing.xs,
    },
    commTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    commTitle: {
      color: colors.textMuted,
      fontSize: 13,
      fontWeight: '600',
    },
    commHint: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 18,
      marginBottom: spacing.xs,
    },
    commRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 10,
    },
    commRowBorder: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    commLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    commLabel: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '500',
    },
    commLabelDisabled: {
      color: colors.textMuted,
    },
    commPhoneHint: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      marginTop: spacing.xs,
    },
    commToggle: {
      width: 44,
      height: 26,
      borderRadius: 13,
      backgroundColor: colors.borderStrong,
      padding: 3,
      justifyContent: 'center',
    },
    commToggleOn: {
      backgroundColor: colors.primary,
    },
    commKnob: {
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: '#FFFFFF',
    },
    commKnobOn: {
      alignSelf: 'flex-end',
    },
    contactEmailInput: {
      marginTop: spacing.xs,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      color: colors.text,
      fontSize: fontSize.md,
    },
  });
}
